import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MDSpinner from "react-md-spinner";
import { toast, ToastContainer } from "react-toastify";
import {
  FaBroadcastTower,
  FaMicrophone,
  FaUsers,
  FaList,
  FaPlus,
  FaInfoCircle,
} from "react-icons/fa";

import Title from "../../components/Title";
import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import { RADIO_HELP_TITLE, RadioHelpContent } from "./radioHelpContent";
import {
  fetchRadioGroups,
  createRadioGroup,
  updateRadioGroup,
  deleteRadioGroup,
  fetchFarmOperators,
} from "./radioApi";
import RadioLivePanel from "./RadioLivePanel";

import "./RadioHome.css";

const TABS = [
  { id: "channels", label: "Canais", icon: FaBroadcastTower },
  { id: "operators", label: "Operadores", icon: FaUsers },
  { id: "audios", label: "Áudios", icon: FaMicrophone },
];

function RadioHome({ t }) {
  const params = useParams();
  const farmId = params.farm_id;

  const [activeTab, setActiveTab] = useState("channels");
  const [loading, setLoading] = useState(true);
  const [operatorsLoading, setOperatorsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [operators, setOperators] = useState([]);
  const [operatorSearch, setOperatorSearch] = useState("");

  // null = criando novo canal; number = editando esse id
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftMemberIds, setDraftMemberIds] = useState([]);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const isCreating = editingGroupId == null;

  const editingGroup = useMemo(
    () => groups.find((item) => Number(item.id) === Number(editingGroupId)),
    [groups, editingGroupId]
  );

  const filteredOperators = useMemo(() => {
    const q = operatorSearch.trim().toLowerCase();
    if (!q) return operators;
    return operators.filter(
      (op) =>
        op.name.toLowerCase().includes(q) ||
        (op.ident || "").toLowerCase().includes(q)
    );
  }, [operatorSearch, operators]);

  const loadGroups = useCallback(async () => {
    const groupsData = await fetchRadioGroups(farmId);
    const nextGroups = groupsData.results || [];
    setGroups(nextGroups);
    return nextGroups;
  }, [farmId]);

  const loadOperators = useCallback(async () => {
    setOperatorsLoading(true);
    try {
      const operatorsData = await fetchFarmOperators(farmId);
      setOperators(operatorsData || []);
      if (!(operatorsData || []).length) {
        toast.warn(
          t(
            "Nenhum operador encontrado nesta fazenda. Cadastre em Cadastros → Operadores."
          )
        );
      }
    } catch (error) {
      console.error(error);
      setOperators([]);
      toast.error(t("Falha ao carregar operadores da plataforma"));
    } finally {
      setOperatorsLoading(false);
    }
  }, [farmId, t]);

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const nextGroups = await loadGroups();
      await loadOperators();
      // Abre o primeiro canal em modo edição, se existir
      if (nextGroups.length) {
        const first = nextGroups[0];
        setEditingGroupId(first.id);
        setDraftName(first.name || "");
        setDraftDescription(first.description || "");
        setDraftMemberIds((first.memberIds || []).map(Number));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao carregar rádio"));
    } finally {
      setLoading(false);
    }
  }, [loadGroups, loadOperators, t]);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  const beginCreate = () => {
    setEditingGroupId(null);
    setDraftName("");
    setDraftDescription("");
    setDraftMemberIds([]);
    setActiveTab("channels");
    toast.info(t("Preencha o novo canal e salve com “Criar canal”."));
  };

  const selectChannelForEdit = (group) => {
    if (!group) return;
    setEditingGroupId(group.id);
    setDraftName(group.name || "");
    setDraftDescription(group.description || "");
    setDraftMemberIds((group.memberIds || []).map(Number));
  };

  const onChannelPickerChange = (rawId) => {
    if (rawId === "" || rawId === "new") {
      beginCreate();
      return;
    }
    const group = groups.find((item) => Number(item.id) === Number(rawId));
    if (group) {
      selectChannelForEdit(group);
    }
  };

  const toggleMember = (operatorId) => {
    const value = Number(operatorId);
    setDraftMemberIds((current) =>
      current.includes(value)
        ? current.filter((id) => id !== value)
        : [...current, value]
    );
  };

  const selectAllOperators = () => {
    setDraftMemberIds(operators.map((op) => Number(op.id)));
  };

  const clearOperators = () => {
    setDraftMemberIds([]);
  };

  const saveGroup = async (event) => {
    if (event) event.preventDefault();
    if (!draftName.trim()) {
      toast.warn(t("Informe o nome do canal (ex.: Canal 1)."));
      setActiveTab("channels");
      return;
    }
    if (!draftMemberIds.length) {
      toast.warn(t("Selecione ao menos um operador na aba Operadores."));
      setActiveTab("operators");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: draftName.trim(),
        description: draftDescription.trim(),
        memberIds: draftMemberIds,
        isActive: true,
      };
      let savedId = editingGroupId;
      if (editingGroupId) {
        await updateRadioGroup(farmId, editingGroupId, payload);
        toast.success(t("Canal atualizado"));
      } else {
        const created = await createRadioGroup(farmId, payload);
        savedId = created.id;
        toast.success(t("Canal criado"));
      }
      const nextGroups = await loadGroups();
      const saved = nextGroups.find((g) => Number(g.id) === Number(savedId));
      if (saved) {
        selectChannelForEdit(saved);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao salvar canal"));
    } finally {
      setSaving(false);
    }
  };

  const removeEditingGroup = async () => {
    if (!editingGroup) return;
    if (
      !window.confirm(
        t(
          "Excluir o canal {{name}}? Os áudios deste canal também serão removidos.",
          { name: editingGroup.name }
        )
      )
    ) {
      return;
    }
    try {
      await deleteRadioGroup(farmId, editingGroup.id);
      toast.success(t("Canal excluído"));
      const nextGroups = await loadGroups();
      if (nextGroups.length) {
        selectChannelForEdit(nextGroups[0]);
      } else {
        beginCreate();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao excluir canal"));
    }
  };

  const renderChannelPicker = (options = {}) => {
    const showNewOption = options.showNewOption !== false;
    const value = isCreating ? "new" : String(editingGroupId || "");
    return (
      <div className="radio-channel-picker">
        <label className="radio-field-label">{t("Canal")}</label>
        <div className="radio-channel-picker-row">
          <select
            className="form-control radio-channel-select"
            value={value}
            onChange={(event) => onChannelPickerChange(event.target.value)}
          >
            {showNewOption ? (
              <option value="new">{t("＋ Novo canal")}</option>
            ) : null}
            {groups.map((group) => (
              <option key={group.id} value={String(group.id)}>
                {group.name}
                {group.description ? ` — ${group.description}` : ""}
                {` (${group.memberCount || 0} ${t("ops.")})`}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={beginCreate}
            title={t("Criar um canal novo (não sobrescreve os existentes)")}
          >
            <FaPlus style={{ marginRight: 4 }} />
            {t("Novo")}
          </button>
        </div>
        <p className="radio-hint radio-hint--tight">
          {isCreating
            ? t(
                "Modo criação: ao salvar, nasce um canal novo. Os canais já existentes não são alterados."
              )
            : t(
                "Modo edição: alterações valem só para o canal selecionado acima."
              )}
        </p>
      </div>
    );
  };

  const renderChannelsTab = () => (
    <div className="radio-layout">
      <div className="radio-panel">
        <h3>
          <FaList style={{ marginRight: 8 }} />
          {t("Canais cadastrados")}
          {groups.length ? ` (${groups.length})` : ""}
        </h3>
        <p className="radio-hint">
          {t("Clique em um canal para editá-lo e definir os operadores.")}
        </p>
        <div className="radio-group-list">
          {groups.length === 0 ? (
            <div className="radio-empty">
              {t("Nenhum canal ainda. Use “Novo” ao lado para criar o Canal 1.")}
            </div>
          ) : (
            groups.map((group) => (
              <button
                type="button"
                key={group.id}
                className={`radio-group-item ${
                  !isCreating && Number(editingGroupId) === Number(group.id)
                    ? "is-active"
                    : ""
                }`}
                onClick={() => {
                  selectChannelForEdit(group);
                  setActiveTab("channels");
                }}
              >
                <strong>{group.name}</strong>
                <small>
                  {group.description || t("Sem descrição")}
                  {" · "}
                  {group.memberCount || 0} {t("operadores")}
                </small>
              </button>
            ))
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm btn-block"
          onClick={beginCreate}
        >
          <FaPlus style={{ marginRight: 6 }} />
          {t("Criar outro canal")}
        </button>
      </div>

      <div className="radio-panel">
        <form className="radio-form-grid" onSubmit={saveGroup}>
          <h3>
            {isCreating
              ? t("Criar canal")
              : `${t("Editar")} · ${editingGroup ? editingGroup.name : ""}`}
          </h3>
          {renderChannelPicker()}
          <label className="radio-field-label">{t("Nome do canal")}</label>
          <input
            className="form-control"
            placeholder={t("Ex.: Canal 1")}
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
          />
          <label className="radio-field-label">{t("Descrição / aplicação")}</label>
          <textarea
            className="form-control"
            rows={2}
            placeholder={t("Ex.: Equipe do app, Cliente, Gerência…")}
            value={draftDescription}
            onChange={(event) => setDraftDescription(event.target.value)}
          />
          <div className="radio-members-summary">
            <strong>
              {draftMemberIds.length} {t("operadores neste canal")}
            </strong>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={() => setActiveTab("operators")}
            >
              {t("Escolher operadores")}
            </button>
          </div>
          <div className="radio-toolbar">
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving}
            >
              {isCreating ? t("Criar canal") : t("Salvar alterações")}
            </button>
            {!isCreating ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={removeEditingGroup}
              >
                {t("Excluir canal")}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );

  const renderOperatorsTab = () => (
    <div className="radio-panel">
      <h3>
        <FaUsers style={{ marginRight: 8 }} />
        {t("Operadores do canal")}
      </h3>
      {renderChannelPicker()}
      <p className="radio-hint">
        {t(
          "Marque os operadores deste canal. Depois volte em Canais e clique em Salvar. Para outro canal, troque no seletor acima."
        )}
      </p>

      <div className="radio-members-summary radio-members-summary--banner">
        {isCreating ? t("Novo canal") : t("Canal")}
        :{" "}
        <strong>
          {draftName || t("(sem nome)")}
        </strong>
        {" — "}
        {draftMemberIds.length} {t("selecionados")}
      </div>

      <div className="radio-toolbar">
        <input
          className="form-control radio-search"
          placeholder={t("Buscar operador…")}
          value={operatorSearch}
          onChange={(event) => setOperatorSearch(event.target.value)}
        />
        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={loadOperators}
          disabled={operatorsLoading}
        >
          {t("Atualizar lista")}
        </button>
        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={selectAllOperators}
          disabled={!operators.length}
        >
          {t("Selecionar todos")}
        </button>
        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={clearOperators}
        >
          {t("Limpar")}
        </button>
      </div>

      {operatorsLoading ? (
        <div className="radio-loading">
          <MDSpinner singleColor="#1ABB9C" size={30} />
        </div>
      ) : (
        <>
          <div className="radio-check-list radio-check-list--tall">
            {filteredOperators.length === 0 ? (
              <div className="radio-empty">
                {operators.length === 0
                  ? t(
                      "Nenhum operador nesta fazenda. Cadastre em Cadastros → Operadores."
                    )
                  : t("Nenhum operador corresponde à busca.")}
              </div>
            ) : (
              filteredOperators.map((operator) => {
                const checked = draftMemberIds.includes(Number(operator.id));
                return (
                  <label
                    key={operator.id}
                    className={`radio-check-item ${
                      checked ? "is-checked" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMember(operator.id)}
                    />
                    <span>
                      {operator.name}
                      {operator.ident ? (
                        <small> ({operator.ident})</small>
                      ) : null}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="radio-toolbar" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={saving}
              onClick={saveGroup}
            >
              {isCreating ? t("Criar canal com estes operadores") : t("Salvar operadores do canal")}
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => setActiveTab("channels")}
            >
              {t("Voltar aos canais")}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderAudiosTab = () => (
    <div>
      <p className="radio-hint">
        {t(
          "Escolha um canal ou Todos os canais. Use Ao vivo ON para tocar novos áudios automaticamente. Use Falar para gravar e enviar o áudio ao campo."
        )}
      </p>
      <RadioLivePanel farmId={farmId} compact={false} />
    </div>
  );

  return (
    <div className="x_content radio-home">
      <div className="radio-home-title-row">
        <Title title={t("Rádio")} farm_name="" add_button="false" />
        <button
          type="button"
          className="radio-home-info-btn"
          title={t("Como usar o rádio?")}
          aria-label={t("Como usar o rádio?")}
          onClick={() => setShowHelpModal(true)}
        >
          <FaInfoCircle />
        </button>
      </div>
      <p className="radio-home-intro">
        {t(
          "1) Selecione ou crie um canal · 2) Aba Operadores para marcar quem entra · 3) Salvar · 4) Áudios para ouvir/enviar. Use “Novo” para criar Canal 2 sem apagar o Canal 1."
        )}
      </p>

      <ul className="nav nav-tabs bar_tabs radio-tabs" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <li
              key={tab.id}
              className={activeTab === tab.id ? "active" : ""}
            >
              <button
                type="button"
                className="radio-tab-button"
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon style={{ marginRight: 6 }} />
                {t(tab.label)}
                {tab.id === "channels" && groups.length
                  ? ` (${groups.length})`
                  : ""}
                {tab.id === "operators" && operators.length
                  ? ` (${operators.length})`
                  : ""}
              </button>
            </li>
          );
        })}
      </ul>

      {loading ? (
        <div className="radio-loading">
          <MDSpinner singleColor="#1ABB9C" size={40} />
        </div>
      ) : (
        <>
          {activeTab === "channels" && renderChannelsTab()}
          {activeTab === "operators" && renderOperatorsTab()}
          {activeTab === "audios" && renderAudiosTab()}
        </>
      )}

      <ToastContainer position="bottom-right" />

      {showHelpModal && (
        <SidebarToolInfoModal
          title={t(RADIO_HELP_TITLE)}
          onClose={() => setShowHelpModal(false)}
        >
          <RadioHelpContent />
        </SidebarToolInfoModal>
      )}
    </div>
  );
}

export default withTranslation()(RadioHome);
